/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { productService } from "@src/@api-services/product";
import { categoryService } from "@src/@api-services/category";
import { subcategoryService } from "@src/@api-services/subcategory";
import { PRODUCT_STATUS } from "../constants";

interface Product {
  id: string | number;
  businessName: string;
  category?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  subCategories?: Array<{ id: number; name: string }>;
  tags?: string[] | null;
  isFeatured?: boolean;
  name: string;
  price: number;
  status?: (typeof PRODUCT_STATUS)[keyof typeof PRODUCT_STATUS];
  createdAt: string;
  updatedAt: string;
  description: string;
  images: any[];
  [key: string]: any;
}

interface GetProductsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  categoryId?: number | string;
  orderBy?: string;
  orderDirection?: "ASC" | "DESC";
  [key: string]: any;
}

interface SelectOption {
  value: number;
  label: string;
}

interface ProductsState {
  data: Product[];
  total: number;
  params: GetProductsParams;
  selectedProduct: Product | null;
  categoryOptions: SelectOption[];
  subcategoryOptions: SelectOption[];
  loading: boolean;
  catalogLoading: boolean;
  error: string | null;
}

export const getProducts = createAsyncThunk(
  "products/getProducts",
  async (params: GetProductsParams, { rejectWithValue }) => {
    try {
      const response = await productService.getProducts(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch products"
      );
    }
  }
);

export const getProduct = createAsyncThunk(
  "products/getProduct",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await productService.getProduct(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch product"
      );
    }
  }
);

export const getProductCategories = createAsyncThunk(
  "products/getProductCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAllCategories({
        orderBy: "name",
        orderDirection: "ASC",
      });
      const rows = response.data?.data ?? [];
      return (Array.isArray(rows) ? rows : []).map((item: any) => ({
        value: Number(item.id),
        label: item.name,
      })) as SelectOption[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories"
      );
    }
  }
);

export const getProductSubcategories = createAsyncThunk(
  "products/getProductSubcategories",
  async (categoryId: number | null, { rejectWithValue }) => {
    try {
      if (!categoryId) return [] as SelectOption[];

      const response = await subcategoryService.getAllSubcategories({
        categoryId,
        orderBy: "name",
        orderDirection: "ASC",
      });
      const rows = response.data?.data ?? [];
      return (Array.isArray(rows) ? rows : []).map((item: any) => ({
        value: Number(item.id),
        label: item.name,
      })) as SelectOption[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategories"
      );
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  "products/updateStatus",
  async (
    {
      id,
      status,
      rejectionReason,
    }: { id: string | number; status: string; rejectionReason?: string },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await productService.updateProductStatus(id, {
        status,
        rejectionReason,
      });
      const state = getState();
      await dispatch(getProducts(state.products.params));
      await dispatch(getProduct(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update product status"
      );
    }
  }
);

export const updateProductCatalogAction = createAsyncThunk(
  "products/updateCatalog",
  async (
    {
      id,
      categoryId,
      subCategoryIds,
      tags,
    }: {
      id: string | number;
      categoryId: number | null;
      subCategoryIds: number[];
      tags: string[]; // stored as JSON string array, no separate table needed
    },
    { dispatch, rejectWithValue }: any
  ) => {
    try {
      await Promise.all([
        productService.updateProductCategory(id, { categoryId }),
        productService.updateProductSubCategories(id, { subCategoryIds }),
        productService.updateProductTags(id, { tags }),
      ]);

      await dispatch(getProduct(id));
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update product catalog"
      );
    }
  }
);

const initialState: ProductsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "id",
    orderDirection: "ASC",
  },
  selectedProduct: null,
  categoryOptions: [],
  subcategoryOptions: [],
  loading: false,
  catalogLoading: false,
  error: null,
};

export const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
      state.subcategoryOptions = [];
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(getProduct.rejected, (state) => {
        state.loading = false;
        state.selectedProduct = null;
      })
      .addCase(getProductCategories.fulfilled, (state, action) => {
        state.categoryOptions = action.payload;
      })
      .addCase(getProductSubcategories.fulfilled, (state, action) => {
        state.subcategoryOptions = action.payload;
      })
      .addCase(getProductSubcategories.rejected, (state) => {
        state.subcategoryOptions = [];
      })
      .addCase(updateProductCatalogAction.pending, (state) => {
        state.catalogLoading = true;
        state.error = null;
      })
      .addCase(updateProductCatalogAction.fulfilled, (state) => {
        state.catalogLoading = false;
      })
      .addCase(updateProductCatalogAction.rejected, (state, action) => {
        state.catalogLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSelectedProduct, setParams } =
  productsSlice.actions;

export default productsSlice.reducer;
